import { makeAutoObservable, runInAction } from "mobx";

import { getUserListByIdFromPost } from "@synthesis/shared-services/auth/user/client";
import type { UserDto } from "@synthesis/shared-services/auth/user/types";
import { logger, naturalSort } from "@synthesis/shared-utils";

import {
  deleteFiles,
  deletePdfFiles,
  getDirectories,
  getDirectoryParentRootId,
  getDirectoryRootByTab,
  postPdfPreviews,
  type DirectoryListResponse,
  type FileMetaDto,
} from "../../../api";
import { DocumentsTypes } from "../../../types";
import { fileOrFolderMatchesTab } from "../../../utils/file-tab-filter";
import { collectUploadProgressRows, mergeServerFilesWithUploadProgress, normalizeBaseName } from "../../../utils/upload-progress";
import { mapPdfPreviewsResponse } from "../../../utils/pdf-preview";
import { workerStore } from "../../file-system-worker-store";
import { DirectoryStore } from "../directory-store";
import { FileStore } from "../file-store";
import {
  type Checked,
  ExplorerView,
  type ExplorerViewValue,
  type History,
  type FileSystemTab,
  type FileSystemViewTab,
  SORTS,
  type SortType,
  StoreState,
  type StoreStateValue,
} from "../root-store";

const initialFolder: History = {
  id: null,
  parentId: null,
  name: "Трехмерные модели",
};

const isAbortError = (err: unknown): boolean => {
  if (err instanceof DOMException && err.name === "AbortError") {
    return true;
  }

  if (typeof err === "object" && err !== null && "code" in err && (err as { code?: string }).code === "ERR_CANCELED") {
    return true;
  }

  // Axios wraps cancellation as an error with message "canceled";
  // normalizeError turns it into ApiError("canceled"), losing the code.
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    return msg === "canceled" || msg === "cancelled" || msg === "request aborted";
  }

  return false;
};

const toFileMetaList = (responseData: DirectoryListResponse[]): FileMetaDto[] =>
  responseData.filter((el) => el.fileMeta && !el.fileMeta.isDeleted).map((el) => el.fileMeta as FileMetaDto);

const DEFAULT_SORT_TYPE: SortType = "file.ASC";

export class GeneralStore {
  fileStore: FileStore;
  directoryStore: DirectoryStore;
  state: StoreStateValue = StoreState.Initial;
  sortByTab: Record<FileSystemTab, SortType> = {
    models: DEFAULT_SORT_TYPE,
    docs: DEFAULT_SORT_TYPE,
    drawings: DEFAULT_SORT_TYPE,
  };
  checkedFiles: Map<string, Checked> = new Map();
  viewByTab: Record<FileSystemViewTab, ExplorerViewValue> = {
    models: ExplorerView.Grid,
    docs: ExplorerView.Grid,
  };
  favorites: Record<string, History[]> = {};
  projectId: string | null = null;
  history: History[] = [];
  activeTab: FileSystemTab = "models";
  pendingRequests: Array<{ abort: () => void }> = [];
  pdfPreviewsByFileId: Map<string, { pageCount: number; previewSrc: string | null }> = new Map();
  authorNamesById: Map<string, string> = new Map();
  isAuthorNamesLoading = false;
  private authorNamesFetchInFlight = new Set<string>();

  constructor(fileStore: FileStore, directoryStore: DirectoryStore, projectId: string) {
    this.fileStore = fileStore;
    this.directoryStore = directoryStore;
    this.projectId = projectId;
    makeAutoObservable(this);
  }

  get sortType(): SortType {
    return this.sortByTab[this.activeTab];
  }

  get view(): ExplorerViewValue {
    if (this.activeTab === "drawings") {
      return ExplorerView.Table;
    }

    return this.viewByTab[this.activeTab];
  }

  private requireProjectId(): string {
    if (!this.projectId) {
      throw new Error("projectId is required");
    }
    return this.projectId;
  }

  abortPendingRequests() {
    this.pendingRequests.forEach((r) => r.abort());
    this.pendingRequests = [];
  }

  async fetchPdfPreviews(pdfFiles: FileMetaDto[]) {
    if (pdfFiles.length === 0) {
      return;
    }

    const results = await Promise.all(
      pdfFiles.map(async (file) => {
        const abortController = new AbortController();
        const pending = { abort: () => abortController.abort() };
        try {
          this.pendingRequests.push(pending);
          const data = (await postPdfPreviews({ path: { fileId: file.id }, body: { pages: [1] }, signal: abortController.signal })).data;

          return { fileId: file.id, ...mapPdfPreviewsResponse(data) };
        } catch (pdfErr) {
          if (!isAbortError(pdfErr)) {
            logger.error("postPdfPreviews >>>", file.id, pdfErr);
          }
          return { fileId: file.id, pageCount: 0, previewSrc: null };
        } finally {
          this.pendingRequests = this.pendingRequests.filter((r) => r !== pending);
        }
      }),
    );
    runInAction(() => {
      const next = new Map(this.pdfPreviewsByFileId);
      results.forEach(({ fileId, pageCount, previewSrc }) => {
        if (pageCount > 0 || previewSrc) {
          next.set(fileId, { pageCount, previewSrc });
        }
      });
      this.pdfPreviewsByFileId = next;
    });
  }

  enrichDocsFiles(files: FileMetaDto[]) {
    if (this.activeTab !== "docs" || files.length === 0) {
      return;
    }

    const pdfFiles = files.filter((f) => f.type === DocumentsTypes.pdf_document);
    void this.fetchAuthorNames(files);

    if (pdfFiles.length > 0) {
      void this.fetchPdfPreviews(pdfFiles);
    }
  }

  getAuthorName(authorId?: string) {
    if (!authorId) {
      return "";
    }

    return this.authorNamesById.get(authorId) ?? authorId;
  }

  getSortFn(type: SortType = this.sortType) {
    if (type === "author.ASC" || type === "author.DESC") {
      const direction = type === "author.ASC" ? "ASC" : "DESC";

      return (a: FileMetaDto, b: FileMetaDto) => {
        const result = naturalSort(this.getAuthorName(a.author), this.getAuthorName(b.author));
        return direction === "DESC" ? -result : result;
      };
    }

    return SORTS.entities[type].sortFn;
  }

  getDirectorySortFn() {
    if (this.sortType === "file.ASC" || this.sortType === "file.DESC") {
      return this.getSortFn();
    }

    return SORTS.entities["file.ASC"].sortFn;
  }

  async fetchAuthorNames(files: FileMetaDto[]) {
    const uniqueIds = [...new Set(files.map((file) => file.author).filter(Boolean))].filter(
      (id): id is string => Boolean(id) && !this.authorNamesById.has(id) && !this.authorNamesFetchInFlight.has(id),
    );

    if (uniqueIds.length === 0) {
      return;
    }

    uniqueIds.forEach((id) => this.authorNamesFetchInFlight.add(id));

    runInAction(() => {
      this.isAuthorNamesLoading = true;
    });

    try {
      const users = (await getUserListByIdFromPost({ body: uniqueIds })).data;

      runInAction(() => {
        const next = new Map(this.authorNamesById);
        users.forEach((user: UserDto) => {
          if (!user.id) {
            return;
          }

          const name = user.lastName ? `${user.lastName} ${user.firstName || ""} ${user.middleName || ""}`.trim() : user.firstName || user.id;
          next.set(user.id, name);
        });
        this.authorNamesById = next;
        this.isAuthorNamesLoading = false;

        if (this.sortType === "author.ASC" || this.sortType === "author.DESC") {
          this.fileStore.files = this.fileStore.files.toSorted(this.getSortFn());
        }
      });
    } catch (err) {
      runInAction(() => {
        this.isAuthorNamesLoading = false;
      });
      logger.error("fetchAuthorNames >>>", err);
    } finally {
      uniqueIds.forEach((id) => this.authorNamesFetchInFlight.delete(id));
    }
  }

  async getContents(parentId?: string | null) {
    this.abortPendingRequests();
    runInAction(() => {
      this.state = StoreState.Loading;
    });

    try {
      let responseData: DirectoryListResponse[];
      let rootFolderId = parentId ?? "";

      if (parentId) {
        const abortController = new AbortController();
        const pending = { abort: () => abortController.abort() };
        this.pendingRequests.push(pending);
        responseData = (await getDirectories({ path: { id: parentId }, signal: abortController.signal })).data;
        this.pendingRequests = this.pendingRequests.filter((r) => r !== pending);
      } else {
        const directoryTab = this.activeTab === "models" ? "IFC" : "PDF";
        const projectId = this.requireProjectId();
        const contentAbort = new AbortController();
        const contentPending = { abort: () => contentAbort.abort() };
        this.pendingRequests.push(contentPending);
        responseData = (await getDirectoryRootByTab({ path: { id: projectId, directoryTab }, signal: contentAbort.signal })).data;
        this.pendingRequests = this.pendingRequests.filter((r) => r !== contentPending);

        const rootAbort = new AbortController();
        const rootPending = { abort: () => rootAbort.abort() };
        this.pendingRequests.push(rootPending);
        const rootMeta = (await getDirectoryParentRootId({ path: { id: projectId }, signal: rootAbort.signal })).data;
        this.pendingRequests = this.pendingRequests.filter((r) => r !== rootPending);
        rootFolderId = rootMeta.id ?? "";
      }

      const data = toFileMetaList(responseData);
      const serverFiles = data.filter(
        (el) =>
          el.type === DocumentsTypes.file ||
          el.type === DocumentsTypes.pdf_document ||
          el.type === DocumentsTypes.model ||
          el.type === DocumentsTypes.cosmos ||
          el.type === DocumentsTypes.cosmos_geoserver,
      );

      runInAction(() => {
        this.directoryStore.directories = data.filter((el) => el.type === DocumentsTypes.folder).toSorted(this.getDirectorySortFn());

        if (this.projectId) {
          serverFiles.forEach((f) => workerStore.clearPendingForProjectBaseName(this.projectId!, normalizeBaseName(f.baseName)));
        }

        if (workerStore.uploading && this.projectId) {
          const progressRows = collectUploadProgressRows(
            this.projectId,
            this.activeTab,
            this.fileStore.files,
            workerStore.getPendingForProject(this.projectId),
          );

          this.fileStore.files = mergeServerFilesWithUploadProgress(serverFiles, progressRows).toSorted(this.getSortFn());
          this.fileStore.allFiles = serverFiles.toSorted(this.getSortFn());
        } else {
          this.fileStore.files = serverFiles.toSorted(this.getSortFn());
          this.fileStore.allFiles = this.fileStore.files;
        }

        this.directoryStore.rootFolderId = parentId ? parentId : rootFolderId;
        this.state = StoreState.Success;
      });

      if (this.activeTab === "docs") {
        this.enrichDocsFiles(serverFiles);
      } else {
        runInAction(() => {
          this.pdfPreviewsByFileId = new Map();
        });
      }
    } catch (err) {
      if (isAbortError(err)) {
        return;
      }
      runInAction(() => {
        this.state = StoreState.Error;
      });
      logger.error("getDirectories >>>", err);
    }
  }

  setSortType(type: SortType) {
    this.sortByTab = { ...this.sortByTab, [this.activeTab]: type };

    this.fileStore.files = this.fileStore.files.toSorted(this.getSortFn());

    this.directoryStore.directories = this.directoryStore.directories.toSorted(this.getDirectorySortFn());
  }

  setState(newState: "loading" | "success") {
    if (newState === "loading") this.state = StoreState.Loading;
    if (newState === "success") this.state = StoreState.Success;
  }

  setCheckedContents(data: Checked) {
    if (!data.id) {
      return;
    }

    const newCheckedFiles = new Map(this.checkedFiles);
    if (newCheckedFiles.has(data.id)) {
      newCheckedFiles.delete(data.id);
    } else {
      newCheckedFiles.set(data.id, { id: data.id, type: data.type, size: data.size });
    }
    this.checkedFiles = newCheckedFiles;
  }

  async deleteCheckedContents(userId: string) {
    this.state = StoreState.Loading;

    const checkedEntries = Array.from(this.checkedFiles.values());
    const pdfIds = checkedEntries.filter((file) => file.type === DocumentsTypes.pdf_document && file.id).map((file) => file.id as string);
    const otherFileIds = checkedEntries
      .filter((file) => file.type !== DocumentsTypes.pdf_document && file.type !== DocumentsTypes.folder && file.id)
      .map((file) => file.id as string);

    try {
      if (pdfIds.length > 0) {
        await deletePdfFiles({ body: { initUserId: userId, ids: pdfIds } });
      }

      if (otherFileIds.length > 0) {
        await deleteFiles({ body: { initUserId: userId, ids: otherFileIds } });
      }
      runInAction(() => {
        if (pdfIds.length > 0) {
          const nextPdfPreviews = new Map(this.pdfPreviewsByFileId);
          pdfIds.forEach((id) => nextPdfPreviews.delete(id));
          this.pdfPreviewsByFileId = nextPdfPreviews;
        }

        this.state = StoreState.Success;
        this.checkedFiles = new Map();
        this.getContents(this.directoryStore.rootFolderId);
      });
    } catch (err) {
      runInAction(() => {
        this.state = StoreState.Error;
      });
      logger.error("deleteFile", err);
    }
  }

  setFilteredContents(data: Checked[]) {
    const newCheckedFiles = new Map<string, Checked>();
    data.forEach((file) => {
      if (!file.id) {
        return;
      }
      newCheckedFiles.set(file.id, { id: file.id, type: file.type, size: file.size });
    });

    const prev = this.checkedFiles;
    if (prev.size === newCheckedFiles.size) {
      let same = true;
      for (const [id, value] of newCheckedFiles) {
        const existing = prev.get(id);
        if (!existing || existing.type !== value.type || existing.size !== value.size) {
          same = false;
          break;
        }
      }
      if (same) {
        return;
      }
    }

    this.checkedFiles = newCheckedFiles;
  }

  setActiveTab(tab: FileSystemTab) {
    if (this.activeTab === tab) {
      return;
    }

    this.activeTab = tab;
    this.checkedFiles = new Map();
    this.getContents(null);
  }

  setCheckedAll() {
    const newCheckedFiles = new Map(this.checkedFiles);
    let all: FileMetaDto[] = [...this.directoryStore.directories, ...this.fileStore.files];
    all = all.filter((el) => fileOrFolderMatchesTab(el, this.activeTab));

    if (all.length === this.checkedFiles.size) {
      newCheckedFiles.clear();
    } else {
      all.forEach((data) => {
        newCheckedFiles.set(data.id, { id: data.id, type: data.type, size: data.size });
      });
    }
    this.checkedFiles = newCheckedFiles;
  }

  resetCheckedAll() {
    this.checkedFiles = new Map();
  }

  setView(view: ExplorerViewValue) {
    if (this.activeTab === "drawings") {
      return;
    }

    this.viewByTab = { ...this.viewByTab, [this.activeTab]: view };
  }

  hydrateViews(views: Partial<Record<FileSystemViewTab, ExplorerViewValue>>) {
    this.viewByTab = {
      models: views.models ?? this.viewByTab.models,
      docs: views.docs ?? this.viewByTab.docs,
    };
  }

  setFavorites(favoritesList: Record<string, History[]>) {
    this.favorites = favoritesList;
  }

  removeFromFavorites(data: History) {
    const projectId = this.directoryStore.projectId;
    const projectFavorites = projectId ? this.favorites[projectId] : undefined;
    if (!projectFavorites) {
      return;
    }

    const removeIndex = projectFavorites.findIndex((el) => el.id === data.id);
    if (removeIndex !== -1) {
      projectFavorites.splice(removeIndex, 1);
    }
  }

  historyReset() {
    this.history = [];
    this.directoryStore.currentFolder = { ...initialFolder };
  }

  historyGo(data: History) {
    if (this.directoryStore.currentFolder) {
      this.history.push(this.directoryStore.currentFolder);
    }
    this.directoryStore.currentFolder = data;
  }

  historyAppendFromRoot(data: History[]) {
    this.historyReset();

    const currentFolder = this.directoryStore.currentFolder;
    if (currentFolder) {
      this.history = [currentFolder, ...data.slice(1)];
    }

    const lastFolder = data.at(-1);
    if (lastFolder) {
      this.directoryStore.currentFolder = { ...lastFolder };
    }
  }

  historyReplace(data: History) {
    const lastIndex = this.history.findIndex((el) => el.id === data.id);
    this.history = this.history.slice(0, lastIndex);
    this.directoryStore.currentFolder = data;
  }

  historyGoBack() {
    const folder = this.history.pop();

    if (folder) {
      this.directoryStore.currentFolder = folder;
    }
  }
}
